import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type RcFile } from 'antd/es/upload'
import { useAtomValue } from 'jotai'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import {
  ERROR_MESSAGES,
  SHIPMENT_ORDER_CSV_IMPORT,
  SUCCESS_MESSAGES
} from '@/constants/common/message.constants'
import { useCustomKeySettingsQuery } from '@/features/custom-key-settings/hooks'
import { toastApiErrorMessage } from '@/features/shared/utils'
import { uploadFile } from '@/features/uploads/api'
import { type UploadFileResult } from '@/features/uploads/models'
import { type CutOutFileDto } from '@/libs/api'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states'

import {
  createGenericShipmentOrderImportJob,
  createShipmentOrderImportFormat,
  deleteShipmentOrderImportFormat,
  updateShipmentOrderImportFormat,
  validateShipmentOrderImport
} from '../../api'
import { useMasterReferenceCandidates } from '../../hooks'
import {
  applySavedFormat,
  buildCustomKeyFieldState,
  buildCutOut,
  buildImportSetting,
  buildPreview,
  buildSavedFormatSetting,
  buildSavedFormatTemplateHeaders,
  buildStandardTemplateHeaders,
  collectUsedValues,
  findMissingSavedColumns,
  isMappingChangedFromSavedFormat,
  deriveStandardMapping,
  deriveStandardMissingRequiredFields,
  findMissingRequiredFields,
  initialCsvReadConfig,
  isPlausiblyStandardFormat,
  isRequiredMappingSatisfied,
  mergeServerCheck,
  parseSavedFormatSetting,
  relabelField,
  sanitizeFileNameSegment,
  siteNameFieldsToShow,
  siteNameTargetsOf,
  STANDARD_CSV_TEMPLATE_FILENAME,
  type CellOverrides,
  type ColumnMapping,
  type CsvImportView,
  type CustomKeyFieldState,
  type CsvReadConfig,
  type CutOut,
  type EffectiveTable,
  type ImportMethod,
  isErrorRow,
  type LogiFieldValue,
  type MasterCandidatesByField,
  type ImportSetting,
  type ParsedCsv,
  type PreviewWithServerCheck,
  type SavedFormat,
  type SavedFormatCheck,
  type SavedFormatSaveChoice,
  type ServerCheck,
  toImportEncoding
} from '../../models'
import { shipmentOrderImportFormatsQueryKeys } from '../../query-keys'
import { getShipmentOrderImportFormatsQueryOptions } from '../../query-options'
import {
  buildEffectiveTable,
  CsvImportError,
  csvRowsToFile,
  downloadCsvRows,
  downloadCsvTemplate,
  type EncodingOverride,
  MAX_CSV_FILE_SIZE,
  parseCsvFile
} from '../../utils'
import { type CutOutKind, type ImportResultSummary } from '../DoneView'
import { type SaveFormatDraft, type ServerCheckStatus } from '../PreviewView'

const EMPTY_EFFECTIVE_TABLE: EffectiveTable = {
  headers: [],
  dataRows: [],
  columnCount: 0
}

// 標準フォーマット経路（skip機構を持たない）に渡す安定参照の空集合
const EMPTY_SKIPPED_FIELDS = new Set<LogiFieldValue>()

/** 確認画面で値を直してから事前チェックをやり直すまでの待ち時間（入力中に何度も走らせない）。 */
const SERVER_RECHECK_DELAY_MS = 1200

export type UseShipmentOrderCsvImportResult = {
  // ステップ表示
  view: CsvImportView
  stepIndex: Record<CsvImportView, number>
  stepItems: { title: string }[]
  /** 「戻る」ボタン用の単純な画面遷移（副作用なし）。 */
  onBack: (target: CsvImportView) => void

  // ステップ0: 取り込み方法
  method: ImportMethod | null
  savedFormats: readonly SavedFormat[]
  selectedFormatId: number | null
  selectedFormat: SavedFormat | null
  canLeaveMethod: boolean
  onChangeMethod: (next: ImportMethod) => void
  onChangeFormatId: (id: number | null) => void
  /** 選んでいる保存フォーマットを削除する（確認は画面側で取る） */
  onDeleteFormat: () => Promise<void>

  // ステップ1: ファイル選択
  parsed: ParsedCsv | null
  readConfig: CsvReadConfig
  parseError: string | null
  encodingOverride: EncodingOverride
  effectiveTable: EffectiveTable
  canLeaveFile: boolean
  onChangeReadConfig: (config: CsvReadConfig) => void
  onSelectFile: (file: File) => Promise<void>
  onChangeEncoding: (override: EncodingOverride) => Promise<void>
  onLeaveFile: () => void
  /**
   * テンプレートCSVダウンロード。
   * standard=固定43列（任意の列「取消」を含む） / saved（選択済み）=保存フォーマットの列名 / adhoc=対象外(false)。
   */
  canDownloadTemplate: boolean
  onDownloadTemplate: () => void

  // ステップ2: マッピング設定（汎用のみ）
  /** 拠点のカスタムキー設定（使えないスロットは候補に出さず、名前は拠点の設定名にする）。 */
  customKeys: CustomKeyFieldState
  mapping: ColumnMapping
  skippedFields: Set<LogiFieldValue>
  canLeaveMapping: boolean
  missingRequiredLabels: string
  /** マッピング画面から進めない理由（進めるときは null）。「次へ」のツールチップに出す */
  leaveMappingBlockedReason: string | null
  onChangeColumn: (columnIndex: number, value: LogiFieldValue | null) => void
  onToggleSkip: (field: LogiFieldValue) => void
  onLeaveMapping: () => void
  /**
   * 保存フォーマットで取り込むときの、定義の確かめ（保存フォーマット以外では null）。
   * CSV に無い保存列があるか、対応づけを変えたら、取り込む前に更新か別名での保存を選ぶ
   */
  savedFormatCheck: SavedFormatCheck | null

  // ステップ3: プレビュー・エラー確認
  isGeneric: boolean
  preview: PreviewWithServerCheck | null
  /** マスタ参照項目（荷主等）の実マスタ検索候補（一括入力パネルの選択肢と存在チェックが共有）。 */
  masterCandidates: MasterCandidatesByField
  standardMissingRequiredFields: ReadonlySet<LogiFieldValue>
  excludeErrors: boolean
  cellOverrides: CellOverrides
  saveFormat: SaveFormatDraft
  canLeavePreview: boolean
  serverCheckStatus: ServerCheckStatus
  onRecheck: () => void
  isExecuting: boolean
  onEditCell: (rowNo: number, field: LogiFieldValue, value: string) => void
  /**
   * 指定項目の値を一括で入れる（#5787）。入れるのはその項目にエラーのある行と、確認画面で直した行だけで、
   * 一度もエラーにならなかった行の値は変えない。rowNos を渡したら、そのうちの対象の行だけ（空なら何も入れない）。
   * 渡さなければ対象の行すべて
   */
  onBulkFill: (
    field: LogiFieldValue,
    value: string,
    rowNos?: readonly number[]
  ) => void
  onChangeExcludeErrors: (value: boolean) => void
  onChangeSaveFormat: (draft: SaveFormatDraft) => void
  onExecute: () => Promise<void>

  // ステップ4: 完了
  result: ImportResultSummary | null
  /** 完了画面から、失敗した行・修正を反映した CSV をダウンロードする */
  onDownloadCutOut: (kind: CutOutKind) => void
}

/**
 * 配送依頼 CSVインポートのウィザード本体のロジック（#4728）。
 *
 * - 最初の分岐は「取り込み方法」の3択: 通常フォーマット / 保存したフォーマット / フォーマット外。
 * - 汎用（保存フォーマット / フォーマット外）は**マッピング画面を常に通る**。
 *   保存フォーマットは初期値の供給源にすぎず、画面を飛ばすショートカットではない。
 * - 確認画面（プレビュー）は3択すべての経路が通る。
 * - フォーマット保存はマッピング内容を次回初期値として残す任意操作（確認画面で指定）。
 * - 状態機械:
 *   standard: method → file → preview → done
 *   saved/adhoc: method → file → mapping → preview → done
 * - ファイルは選んだ時点で一時領域にアップロードし、確認画面では事前チェック API（取り込まずに
 *   サーバー側の検証だけを走らせる）の結果を画面側の形式チェックに重ねる。値を直すと少し待って
 *   自動で確かめ直す。
 * - 取り込み実行はジョブ投入（非同期）。マッピング・確認画面での修正・除外を「取り込み設定」として
 *   一緒に送る（通常フォーマットも同じ。サーバーは事前チェックと同じ読み方でジョブを処理する）。
 * - 保存フォーマットは拠点単位でサーバーに保存する。
 *
 * view（ShipmentOrderCsvImportModalContent.view.tsx）はこのフックの返り値だけで描画する
 * （frontend-coding.md §1: view/hook/container 分割）。
 */
/** 確認画面で外した行の理由（rowNo → 「項目: 理由」を「 / 」でつないだもの）。失敗した行の CSV の末尾の列に書く */
const failedReasonsOf = (
  preview: PreviewWithServerCheck
): Map<number, string> => {
  const labelOf = new Map(preview.fields.map((f) => [f.value, f.label]))
  return new Map(
    preview.rows
      .filter(isErrorRow)
      .map((row) => [
        row.rowNo,
        [
          ...row.errors.map(
            (e) => `${labelOf.get(e.field) ?? e.field}: ${e.reason}`
          ),
          ...row.rowErrors
        ].join(' / ')
      ])
  )
}

const unionFields = (
  a: ReadonlySet<LogiFieldValue>,
  b: ReadonlySet<LogiFieldValue>
): ReadonlySet<LogiFieldValue> => (b.size === 0 ? a : new Set([...a, ...b]))

/** 切り出しの種類ごとに、書き出す CSV の行を組み立てる（完了画面のダウンロードと、インポート履歴に残すアップロードで共用） */
const cutOutRowsOf = (kind: CutOutKind, cutOut: CutOut): string[][] =>
  kind === 'failed'
    ? [...cutOut.failedLeadingRows, ...cutOut.failedRows]
    : [...cutOut.leadingRows, ...cutOut.importedRows]

/** 取り出せる件数。修正を反映した CSV は、CSV にある列を直したときだけ出す（直していなければ元の CSV と同じ） */
const cutOutCountsOf = (cutOut: CutOut): Record<CutOutKind, number> => ({
  failed: cutOut.failedRows.length,
  imported: cutOut.editedRowCount > 0 ? cutOut.importedRows.length : 0
})

export const useShipmentOrderCsvImport = (
  tenantKey: string
): UseShipmentOrderCsvImportResult => {
  const { candidates: masterCandidates } =
    useMasterReferenceCandidates(tenantKey)
  const dcId = useAtomValue(selectedDcIdAtom)
  const queryClient = useQueryClient()
  const { data: customKeySettings } = useCustomKeySettingsQuery(tenantKey, dcId)
  const customKeys = useMemo(
    () => buildCustomKeyFieldState(customKeySettings),
    [customKeySettings]
  )
  const { data: formatEntities } = useQuery({
    ...getShipmentOrderImportFormatsQueryOptions({
      tenantKey,
      dcId: dcId ?? 0
    }),
    enabled: dcId !== null
  })
  const savedFormats = useMemo<readonly SavedFormat[]>(
    () =>
      (formatEntities ?? []).flatMap((entity) => {
        const setting = parseSavedFormatSetting(entity.setting)
        return setting ? [{ id: entity.id, name: entity.name, setting }] : []
      }),
    [formatEntities]
  )
  const [view, setView] = useState<CsvImportView>('method')
  const [method, setMethod] = useState<ImportMethod | null>(null)
  const [selectedFormatId, setSelectedFormatId] = useState<number | null>(null)
  const [savedFormatChoice, setSavedFormatChoice] =
    useState<SavedFormatSaveChoice | null>(null)
  const [savedFormatNewName, setSavedFormatNewName] = useState('')
  const [parsed, setParsed] = useState<ParsedCsv | null>(null)
  const [readConfig, setReadConfig] =
    useState<CsvReadConfig>(initialCsvReadConfig)
  const [parseError, setParseError] = useState<string | null>(null)
  const [rawFile, setRawFile] = useState<File | null>(null)
  const [encodingOverride, setEncodingOverride] =
    useState<EncodingOverride>('auto')
  const [mapping, setMapping] = useState<ColumnMapping>({})
  const [skippedFields, setSkippedFields] = useState<Set<LogiFieldValue>>(
    new Set()
  )
  const [excludeErrors, setExcludeErrors] = useState(true)
  const [cellOverrides, setCellOverrides] = useState<CellOverrides>({})
  const [saveFormat, setSaveFormat] = useState<SaveFormatDraft>({
    enabled: false,
    name: ''
  })
  const [result, setResult] = useState<ImportResultSummary | null>(null)
  const [cutOut, setCutOut] = useState<CutOut | null>(null)
  const [uploaded, setUploaded] = useState<UploadFileResult | null>(null)
  const [serverCheck, setServerCheck] = useState<ServerCheck | null>(null)
  const [serverCheckStatus, setServerCheckStatus] =
    useState<ServerCheckStatus>('idle')
  const [isExecuting, setIsExecuting] = useState(false)
  const serverCheckAbortRef = useRef<AbortController | null>(null)
  // 最後に事前チェックへ渡した修正値（同じ内容で重ねて確かめないため）
  const lastCheckedOverridesRef = useRef<CellOverrides | null>(null)

  const isGeneric = method === 'saved' || method === 'adhoc'
  const selectedFormat = useMemo(
    () => savedFormats.find((f) => f.id === selectedFormatId) ?? null,
    [savedFormats, selectedFormatId]
  )

  // フォーマット・ファイルを選び直したら、保存し直しの選択は持ち越さない
  useEffect(() => {
    setSavedFormatChoice(null)
    setSavedFormatNewName('')
  }, [selectedFormatId, parsed])

  // 列の顔ぶれが変わったらマッピングをリセットする。
  useEffect(() => {
    setMapping({})
    setSkippedFields(new Set())
  }, [parsed, readConfig.startColNo])

  // 行の顔ぶれ・取り込み方法が変わったらインライン修正をリセットする
  useEffect(() => {
    setCellOverrides({})
  }, [parsed, readConfig.startColNo, readConfig.startRowNo, method])

  // 取り込み方法を選び直したら、選んだファイルも持ち越さない。方法ごとに使う CSV が違うのが普通で、
  // 前のファイルが残っていると、そのまま別の方法で取り込めてしまう（サントリー様の選択肢も同じ扱い）
  const onChangeMethod = useCallback((next: ImportMethod) => {
    setMethod(next)
    setRawFile(null)
    setParsed(null)
    setParseError(null)
    setEncodingOverride('auto')
    setReadConfig(initialCsvReadConfig)
    setUploaded(null)
    setServerCheck(null)
    setServerCheckStatus('idle')
    setMapping({})
    setSkippedFields(new Set())
    setSaveFormat({ enabled: false, name: '' })
  }, [])

  const onChangeColumn = useCallback(
    (columnIndex: number, value: LogiFieldValue | null) => {
      setMapping((prev) => ({ ...prev, [columnIndex]: value }))
      if (value !== null) {
        setSkippedFields((prev) => {
          if (!prev.has(value)) return prev
          const next = new Set(prev)
          next.delete(value)
          return next
        })
      }
    },
    []
  )

  const onToggleSkip = useCallback((field: LogiFieldValue) => {
    setSkippedFields((prev) => {
      const next = new Set(prev)
      if (next.has(field)) next.delete(field)
      else next.add(field)
      return next
    })
  }, [])

  const parseAndSet = useCallback(
    async (file: File, override: EncodingOverride) => {
      setParseError(null)
      try {
        const parseResult = await parseCsvFile(
          file,
          override === 'auto' ? undefined : override
        )
        setParsed(parseResult)
      } catch (error) {
        setParsed(null)
        setParseError(
          error instanceof CsvImportError && error.kind === 'too-large'
            ? SHIPMENT_ORDER_CSV_IMPORT.FILE_TOO_LARGE(
                MAX_CSV_FILE_SIZE / (1024 * 1024)
              )
            : SHIPMENT_ORDER_CSV_IMPORT.PARSE_ERROR
        )
      }
    },
    []
  )

  const upload = useCallback(async (file: File) => {
    setUploaded(null)
    try {
      // uploadFile は antd Upload の RcFile を受け取るが、使うのは File としての中身と name だけ
      setUploaded(await uploadFile(file as RcFile))
    } catch (error) {
      toastApiErrorMessage(error, SHIPMENT_ORDER_CSV_IMPORT.UPLOAD_FAILED)
    }
  }, [])

  const onSelectFile = useCallback(
    async (file: File) => {
      setRawFile(file)
      setEncodingOverride('auto')
      // 保存フォーマットを使うときは、保存時の読み取り設定（ヘッダー行・開始行・開始列）から始める
      setReadConfig(
        method === 'saved' && selectedFormat
          ? selectedFormat.setting.readConfig
          : initialCsvReadConfig
      )
      setServerCheck(null)
      setServerCheckStatus('idle')
      await Promise.all([parseAndSet(file, 'auto'), upload(file)])
    },
    [parseAndSet, upload, method, selectedFormat]
  )

  const onChangeEncoding = useCallback(
    async (override: EncodingOverride) => {
      setEncodingOverride(override)
      if (rawFile) {
        await parseAndSet(rawFile, override)
      }
    },
    [rawFile, parseAndSet]
  )

  const effectiveTable = useMemo<EffectiveTable>(() => {
    if (!parsed) return EMPTY_EFFECTIVE_TABLE
    return buildEffectiveTable(parsed, readConfig)
  }, [parsed, readConfig])

  const canLeaveMethod =
    method !== null && (method !== 'saved' || selectedFormat !== null)
  const canLeaveFile = parsed !== null

  // テンプレートCSVダウンロード: standardは固定列、savedは選択済みフォーマットの列名。
  // adhocはマッピングを事前に持たないため対象外（テンプレという概念が成立しない）。
  const templateDownload = useMemo(() => {
    if (method === 'standard') {
      return {
        filename: STANDARD_CSV_TEMPLATE_FILENAME,
        headers: buildStandardTemplateHeaders()
      }
    }
    if (method === 'saved' && selectedFormat) {
      return {
        filename: `${sanitizeFileNameSegment(selectedFormat.name)}_template.csv`,
        headers: buildSavedFormatTemplateHeaders(selectedFormat)
      }
    }
    return null
  }, [method, selectedFormat])

  const onDownloadTemplate = useCallback(() => {
    if (!templateDownload) return
    downloadCsvTemplate(templateDownload.filename, templateDownload.headers)
  }, [templateDownload])

  const usedValues = useMemo(() => collectUsedValues(mapping), [mapping])
  const savedFormatCheck = useMemo<SavedFormatCheck | null>(() => {
    if (method !== 'saved' || !selectedFormat || !parsed) return null
    const missingColumns = findMissingSavedColumns(
      effectiveTable,
      selectedFormat
    ).map(
      (column) =>
        column.header ??
        SHIPMENT_ORDER_CSV_IMPORT.COLUMN_LABEL(column.columnIndex)
    )
    const isEdited = isMappingChangedFromSavedFormat(
      effectiveTable,
      selectedFormat,
      mapping
    )
    const trimmed = savedFormatNewName.trim()
    return {
      formatName: selectedFormat.name,
      missingColumns,
      isEdited,
      needsSave: missingColumns.length > 0 || isEdited,
      choice: savedFormatChoice,
      newName: savedFormatNewName,
      isNewNameDuplicated:
        trimmed !== '' && savedFormats.some((f) => f.name === trimmed),
      onChangeChoice: setSavedFormatChoice,
      onChangeNewName: setSavedFormatNewName
    }
  }, [
    method,
    selectedFormat,
    parsed,
    effectiveTable,
    mapping,
    savedFormatChoice,
    savedFormatNewName,
    savedFormats
  ])

  // 保存フォーマットで取り込むときは、定義を直したら（CSV に無い列があるときも）保存し直しを選ぶまで進めない
  const isSavedFormatSaveChosen =
    !savedFormatCheck?.needsSave ||
    savedFormatCheck.choice === 'update' ||
    (savedFormatCheck.choice === 'new' &&
      savedFormatCheck.newName.trim() !== '' &&
      !savedFormatCheck.isNewNameDuplicated)
  const canLeaveMapping = useMemo(
    () =>
      isRequiredMappingSatisfied(usedValues, skippedFields) &&
      isSavedFormatSaveChosen,
    [usedValues, skippedFields, isSavedFormatSaveChosen]
  )
  const missingRequiredLabels = useMemo(
    () =>
      findMissingRequiredFields(usedValues, skippedFields)
        .map((f) => f.label)
        .join('、'),
    [usedValues, skippedFields]
  )
  // 必須項目が足りないのか、保存し直しを選んでいないのかで、進めない理由の案内を分ける
  const leaveMappingBlockedReason = canLeaveMapping
    ? null
    : missingRequiredLabels !== ''
      ? SHIPMENT_ORDER_CSV_IMPORT.MAPPING_MISSING_REQUIRED(
          missingRequiredLabels
        )
      : SHIPMENT_ORDER_CSV_IMPORT.SAVED_FORMAT_SAVE_REQUIRED

  const onBack = useCallback((target: CsvImportView) => setView(target), [])

  const onLeaveFile = useCallback(() => {
    if (method === 'standard') {
      setView('preview')
      return
    }
    if (
      method === 'saved' &&
      selectedFormat &&
      Object.keys(mapping).length === 0
    ) {
      setMapping(applySavedFormat(effectiveTable, selectedFormat))
    }
    setView('mapping')
  }, [method, selectedFormat, mapping, effectiveTable])

  const onLeaveMapping = useCallback(() => {
    // 保存フォーマットで取り込むときの保存は、マッピング画面で選んだとおりにする（確認画面では選ばせない）
    if (method === 'saved' && savedFormatCheck) {
      setSaveFormat(
        !savedFormatCheck.needsSave
          ? { enabled: false, name: '' }
          : {
              enabled: true,
              name:
                savedFormatCheck.choice === 'update'
                  ? savedFormatCheck.formatName
                  : savedFormatCheck.newName.trim()
            }
      )
    }
    setView('preview')
  }, [method, savedFormatCheck])

  const onDeleteFormat = useCallback(async () => {
    if (!selectedFormat) return
    try {
      await deleteShipmentOrderImportFormat(tenantKey, selectedFormat.id)
      toast.success(
        SHIPMENT_ORDER_CSV_IMPORT.SAVED_FORMAT_DELETED(selectedFormat.name)
      )
      setSelectedFormatId(null)
      await queryClient.invalidateQueries({
        queryKey: shipmentOrderImportFormatsQueryKeys.all
      })
    } catch (error) {
      toastApiErrorMessage(
        error,
        SHIPMENT_ORDER_CSV_IMPORT.SAVED_FORMAT_DELETE_FAILED
      )
    }
  }, [selectedFormat, tenantKey, queryClient])

  const standardMapping = useMemo<ColumnMapping | null>(() => {
    if (method !== 'standard' || !parsed) return null
    return isPlausiblyStandardFormat(effectiveTable)
      ? deriveStandardMapping(effectiveTable)
      : null
  }, [method, parsed, effectiveTable])

  const standardMissingRequiredFields = useMemo(
    () =>
      standardMapping
        ? deriveStandardMissingRequiredFields(standardMapping)
        : EMPTY_SKIPPED_FIELDS,
    [standardMapping]
  )

  // 自動作成する地点の名前（発地名称・着地名称）を確認画面で入れられるように、名称の列が無くても
  // その項目を表に出す。事前チェックが名前の無い地点を名称の項目の問題として返したとき、または
  // 名前を入れた後（入れ終わって問題が消えても、入れた値を見て直せるように）に出す
  const siteNameFields = useMemo(
    () => siteNameFieldsToShow(serverCheck?.issues ?? [], cellOverrides),
    [serverCheck, cellOverrides]
  )

  const basePreview = useMemo(() => {
    if (!parsed) return null
    if (isGeneric) {
      if (Object.keys(mapping).length === 0 && skippedFields.size === 0) {
        return null
      }
      return buildPreview(
        effectiveTable,
        mapping,
        cellOverrides,
        unionFields(skippedFields, siteNameFields),
        masterCandidates
      )
    }
    if (!standardMapping) return null
    return buildPreview(
      effectiveTable,
      standardMapping,
      cellOverrides,
      unionFields(standardMissingRequiredFields, siteNameFields),
      masterCandidates
    )
  }, [
    isGeneric,
    parsed,
    effectiveTable,
    mapping,
    standardMapping,
    standardMissingRequiredFields,
    cellOverrides,
    skippedFields,
    siteNameFields,
    masterCandidates
  ])

  // 画面の表示名に拠点のカスタムキー名を反映し、事前チェックの結果を重ねる
  const preview = useMemo<PreviewWithServerCheck | null>(() => {
    if (!basePreview) return null
    const relabeled = {
      ...basePreview,
      fields: basePreview.fields.map((f) => relabelField(f, customKeys))
    }
    return mergeServerCheck(relabeled, serverCheck, cellOverrides)
  }, [basePreview, customKeys, serverCheck, cellOverrides])

  const importEncoding = parsed ? toImportEncoding(parsed.encodingCode) : null

  const errorRowNos = useMemo(
    () => (preview?.rows ?? []).filter(isErrorRow).map((r) => r.rowNo),
    [preview]
  )

  /** 取り込み設定を組み立てる。excludedRowNos は事前チェックでは空（全行の問題を見たいので）。 */
  const buildSetting = useCallback(
    (
      excludedRowNos: number[],
      formatName: string | null
    ): ImportSetting | null => {
      if (!importEncoding) return null
      return buildImportSetting({
        isStandard: method === 'standard',
        encoding: importEncoding,
        readConfig,
        mapping,
        overrides: cellOverrides,
        excludedRowNos,
        formatName
      })
    },
    [importEncoding, method, readConfig, mapping, cellOverrides]
  )

  const runServerCheck = useCallback(async () => {
    if (!uploaded || dcId === null || !basePreview) return
    const setting = buildSetting([], null)
    if (!setting) return
    serverCheckAbortRef.current?.abort()
    const controller = new AbortController()
    serverCheckAbortRef.current = controller
    const checkedOverrides = cellOverrides
    lastCheckedOverridesRef.current = checkedOverrides
    setServerCheckStatus('checking')
    try {
      const result = await validateShipmentOrderImport(
        tenantKey,
        { fileKey: uploaded.key, dcId, setting },
        controller.signal
      )
      if (controller.signal.aborted) return
      setServerCheck({
        issues: result.issues.map((issue) => ({
          rowNo: issue.rowNo ?? null,
          field: (issue.field ?? null) as LogiFieldValue | null,
          message: issue.message
        })),
        siteAutoCreates: result.siteAutoCreates.map((s) => ({
          rowNo: s.rowNo,
          field: s.field as 'originSiteId' | 'destSiteId',
          code: s.code
        })),
        checkedOverrides
      })
      setServerCheckStatus('done')
    } catch (error) {
      if (controller.signal.aborted) return
      setServerCheckStatus('failed')
    }
  }, [uploaded, dcId, basePreview, buildSetting, cellOverrides, tenantKey])

  // 確認画面に入ったとき・読み方（マッピング等）が変わったときはすぐ、値を直したときは少し待って確かめる
  const latestRunServerCheck = useRef(runServerCheck)
  latestRunServerCheck.current = runServerCheck
  const isPreviewReady = view === 'preview' && basePreview !== null
  useEffect(() => {
    if (!isPreviewReady) return
    void latestRunServerCheck.current()
  }, [isPreviewReady, uploaded, mapping, readConfig, skippedFields, method])
  useEffect(() => {
    if (!isPreviewReady) return
    if (lastCheckedOverridesRef.current === cellOverrides) return
    const timer = setTimeout(() => {
      void latestRunServerCheck.current()
    }, SERVER_RECHECK_DELAY_MS)
    return () => clearTimeout(timer)
  }, [isPreviewReady, cellOverrides])
  useEffect(() => () => serverCheckAbortRef.current?.abort(), [])

  const onRecheck = useCallback(() => {
    void runServerCheck()
  }, [runServerCheck])

  const canLeavePreview =
    !(method === 'standard' && preview === null) &&
    uploaded !== null &&
    importEncoding !== null &&
    dcId !== null &&
    !isExecuting &&
    preview !== null &&
    preview.globalIssues.length === 0 &&
    // エラー行を残したまま送るとジョブ全体が失敗するので、除外しない限り送れない
    (errorRowNos.length === 0 || excludeErrors) &&
    preview.okCount > 0 &&
    // 「確認画面で問題が無ければ取り込める」を保つため、今の修正値で事前チェックが済んでいる時だけ送る
    // （確認中・失敗・値を直した直後の再チェック待ちの間は、画面の結果が取込の結果と一致する保証が無い）
    serverCheckStatus === 'done' &&
    serverCheck?.checkedOverrides === cellOverrides

  const onEditCell = useCallback(
    (rowNo: number, field: LogiFieldValue, value: string) => {
      // 自動作成する地点の名前は地点ごとに 1 つなので、同じコードで地点を作る行すべてに同じ名前を入れる
      const targets = siteNameTargetsOf(
        serverCheck?.siteAutoCreates ?? [],
        rowNo,
        field
      )
      setCellOverrides((prev) => {
        const next = { ...prev }
        for (const target of targets) {
          next[target.rowNo] = { ...next[target.rowNo], [target.field]: value }
        }
        return next
      })
    },
    [serverCheck]
  )

  const onBulkFill = useCallback(
    (field: LogiFieldValue, value: string, rowNos?: readonly number[]) => {
      if (!preview) return
      // 入れるのはその項目にエラーのある行と、確認画面で直した行だけ（列がある項目で、正しく入っている値を
      // 上書きしない。直した行は、エラーのセルと同じく直し続けられるようにする）。
      // 行を渡したら、そのうちの対象の行だけ。空の行の一覧は「どの行にも入れない」として扱う
      const errorRowNos = preview.rows
        .filter(
          (row) =>
            row.errors.some((error) => error.field === field) ||
            row.editedFields.includes(field)
        )
        .map((row) => row.rowNo)
      const selected = rowNos === undefined ? null : new Set(rowNos)
      const targets =
        selected === null
          ? errorRowNos
          : errorRowNos.filter((rowNo) => selected.has(rowNo))
      setCellOverrides((prev) => {
        const next = { ...prev }
        for (const rowNo of targets) {
          next[rowNo] = { ...next[rowNo], [field]: value }
        }
        return next
      })
    },
    [preview]
  )

  /** 保存フォーマットを保存する（同じ名前が拠点にあれば上書き）。失敗しても取り込み自体は止めない。 */
  const saveFormatIfRequested = useCallback(async (): Promise<
    string | null
  > => {
    const name = saveFormat.name.trim()
    if (!isGeneric || !saveFormat.enabled || name === '' || dcId === null) {
      return null
    }
    const setting = buildSavedFormatSetting(effectiveTable, readConfig, mapping)
    const existing = savedFormats.find((f) => f.name === name)
    try {
      if (existing) {
        await updateShipmentOrderImportFormat(tenantKey, existing.id, {
          name,
          setting
        })
      } else {
        await createShipmentOrderImportFormat(tenantKey, {
          dcId,
          name,
          setting
        })
      }
      await queryClient.invalidateQueries({
        queryKey: shipmentOrderImportFormatsQueryKeys.all
      })
      return name
    } catch (error) {
      toastApiErrorMessage(error, SHIPMENT_ORDER_CSV_IMPORT.SAVE_FORMAT_FAILED)
      return null
    }
  }, [
    saveFormat,
    isGeneric,
    dcId,
    effectiveTable,
    readConfig,
    mapping,
    savedFormats,
    tenantKey,
    queryClient
  ])

  // 失敗した行と修正を反映した CSV（あるものだけ）を一時領域に上げ、インポート履歴に残す（#5737）。
  // 上げられなくても取り込みは止めない（完了画面からはダウンロードできる）
  const uploadCutOutFiles = useCallback(
    async (
      builtCutOut: CutOut,
      source: ParsedCsv
    ): Promise<CutOutFileDto[] | undefined> => {
      const baseName = source.fileName.replace(/\.[^.]+$/, '')
      const counts = cutOutCountsOf(builtCutOut)
      const targets = (
        [
          { kind: 'failed', dtoKind: 'FAILED' },
          { kind: 'imported', dtoKind: 'IMPORTED' }
        ] as const
      ).filter(({ kind }) => counts[kind] > 0)
      if (targets.length === 0) return undefined
      try {
        return await Promise.all(
          targets.map(async ({ kind, dtoKind }) => {
            const { key, name } = await uploadFile(
              csvRowsToFile(
                `${baseName}${SHIPMENT_ORDER_CSV_IMPORT.CUT_OUT_FILE_SUFFIX[kind]}.csv`,
                cutOutRowsOf(kind, builtCutOut),
                source.encodingCode
              ) as RcFile
            )
            return { kind: dtoKind, key, name }
          })
        )
      } catch (error) {
        toastApiErrorMessage(
          error,
          SHIPMENT_ORDER_CSV_IMPORT.CUT_OUT_UPLOAD_FAILED
        )
        return undefined
      }
    },
    []
  )

  const onExecute = useCallback(async () => {
    if (!canLeavePreview || !uploaded || dcId === null || !preview) return
    const excludedRowNos = excludeErrors ? errorRowNos : []
    const formatName =
      isGeneric && saveFormat.enabled ? saveFormat.name.trim() || null : null
    // フォーマットの保存が終わるまで実行中のままにする（保存の応答待ちの間に実行ボタンを
    // もう一度押せると、同じファイルの取込ジョブが二重にできる）
    setIsExecuting(true)
    try {
      // 通常フォーマットも取り込み設定（kind='standard'）付きで送る。事前チェックと同じ読み方で
      // ジョブを処理させ、確認画面で問題が無ければ取り込めることを保証する（従来の通常取込は
      // 基本の列がすべて揃っていないとジョブごと失敗するため、確認画面の判定と食い違う）
      const setting = buildSetting(excludedRowNos, formatName)
      if (!setting) return
      // 失敗した行と修正を反映した CSV を、元の CSV の形で切り出しておく（完了画面とインポート履歴から取り出す）
      const cutOutMapping = isGeneric ? mapping : standardMapping
      const builtCutOut =
        parsed && cutOutMapping
          ? buildCutOut({
              parsed,
              readConfig,
              mapping: cutOutMapping,
              overrides: cellOverrides,
              excludedRowNos,
              failedReasons: failedReasonsOf(preview)
            })
          : null
      const cutOutFiles =
        builtCutOut && parsed
          ? await uploadCutOutFiles(builtCutOut, parsed)
          : undefined
      try {
        await createGenericShipmentOrderImportJob(tenantKey, {
          fileKey: uploaded.key,
          fileName: uploaded.name,
          dcId,
          setting,
          ...(cutOutFiles ? { cutOutFiles } : {})
        })
      } catch (error) {
        toastApiErrorMessage(
          error,
          ERROR_MESSAGES.FAILED_TO_IMPORT_SHIPMENT_ORDER
        )
        return
      }
      toast.success(SUCCESS_MESSAGES.STARTED_IMPORTING_SHIPMENT_ORDER)
      const savedFormatName = await saveFormatIfRequested()
      setCutOut(builtCutOut)
      setResult({
        registeredCount: preview.rows.length - excludedRowNos.length,
        excludedCount: excludedRowNos.length,
        savedFormatName,
        cutOutCounts: builtCutOut ? cutOutCountsOf(builtCutOut) : null
      })
      setView('done')
    } finally {
      setIsExecuting(false)
    }
  }, [
    canLeavePreview,
    uploaded,
    dcId,
    preview,
    excludeErrors,
    errorRowNos,
    isGeneric,
    saveFormat,
    tenantKey,
    buildSetting,
    saveFormatIfRequested,
    uploadCutOutFiles,
    mapping,
    standardMapping,
    parsed,
    readConfig,
    cellOverrides
  ])

  const onDownloadCutOut = useCallback(
    (kind: CutOutKind) => {
      if (!cutOut || !parsed) return
      const baseName = parsed.fileName.replace(/\.[^.]+$/, '')
      const rows = cutOutRowsOf(kind, cutOut)
      downloadCsvRows(
        `${baseName}${SHIPMENT_ORDER_CSV_IMPORT.CUT_OUT_FILE_SUFFIX[kind]}.csv`,
        rows,
        parsed.encodingCode
      )
    },
    [cutOut, parsed]
  )

  // 1 段目は「取り込み方法・ファイル選択」（方法を選ぶと同じ画面の下にファイル選択が出る）。
  // 通常フォーマットはマッピングが無いので 3 段、保存したフォーマット・フォーマット外は 4 段
  const stepIndex: Record<CsvImportView, number> =
    method === 'standard'
      ? { method: 0, mapping: 0, preview: 1, done: 2 }
      : { method: 0, mapping: 1, preview: 2, done: 3 }
  const stepItems =
    method === 'standard'
      ? [
          { title: SHIPMENT_ORDER_CSV_IMPORT.STEP_METHOD },
          { title: SHIPMENT_ORDER_CSV_IMPORT.STEP_PREVIEW },
          { title: SHIPMENT_ORDER_CSV_IMPORT.STEP_EXECUTE }
        ]
      : [
          { title: SHIPMENT_ORDER_CSV_IMPORT.STEP_METHOD },
          { title: SHIPMENT_ORDER_CSV_IMPORT.STEP_MAPPING },
          { title: SHIPMENT_ORDER_CSV_IMPORT.STEP_PREVIEW },
          { title: SHIPMENT_ORDER_CSV_IMPORT.STEP_EXECUTE }
        ]

  return {
    view,
    stepIndex,
    stepItems,
    onBack,

    method,
    savedFormats,
    selectedFormatId,
    selectedFormat,
    canLeaveMethod,
    onChangeMethod,
    onChangeFormatId: setSelectedFormatId,
    onDeleteFormat,

    parsed,
    readConfig,
    parseError,
    encodingOverride,
    effectiveTable,
    canLeaveFile,
    onChangeReadConfig: setReadConfig,
    onSelectFile,
    onChangeEncoding,
    onLeaveFile,
    canDownloadTemplate: templateDownload !== null,
    onDownloadTemplate,

    customKeys,
    mapping,
    skippedFields,
    canLeaveMapping,
    missingRequiredLabels,
    leaveMappingBlockedReason,
    onChangeColumn,
    onToggleSkip,
    onLeaveMapping,
    savedFormatCheck,

    isGeneric,
    preview,
    masterCandidates,
    standardMissingRequiredFields,
    excludeErrors,
    cellOverrides,
    saveFormat,
    canLeavePreview,
    serverCheckStatus,
    onRecheck,
    isExecuting,
    onEditCell,
    onBulkFill,
    onChangeExcludeErrors: setExcludeErrors,
    onChangeSaveFormat: setSaveFormat,
    onExecute,

    result,
    onDownloadCutOut
  }
}
